/**
 * Analysis Card
 *
 * A compact card wrapper for analysis sections.
 * Similar to DashboardCard but with tighter spacing.
 */

import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

interface AnalysisCardProps {
  /** Card title - can be string or JSX */
  title: ReactNode;
  /** Optional subtitle */
  subtitle?: string;
  /** Optional action element (button, link, etc.) */
  action?: ReactNode;
  /** Card content */
  children: ReactNode;
  /** Additional className */
  className?: string;
  /** Remove padding from content area */
  noPadding?: boolean;
}

export function AnalysisCard({
  title,
  subtitle,
  action,
  children,
  className,
  noPadding = false,
}: AnalysisCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg border border-border/50 bg-card shadow-sm",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/30">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {subtitle && (
            <p className="text-[10px] text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {action && <div>{action}</div>}
      </div>

      {/* Content */}
      <div className={cn(noPadding ? "" : "p-3")}>{children}</div>
    </div>
  );
}

/**
 * Simple section divider
 */
export function SectionDivider({ className }: { className?: string }) {
  return <div className={cn("border-t border-border/30 my-3", className)} />;
}

/**
 * Empty state for cards with no data
 */
interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-8 text-center",
        className
      )}
    >
      {icon && (
        <div className="text-muted-foreground/50 mb-2 text-2xl">{icon}</div>
      )}
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      {description && (
        <p className="text-xs text-muted-foreground/70 mt-1 max-w-xs">
          {description}
        </p>
      )}
    </div>
  );
}

/**
 * Insight banner for contextual recommendations
 */
interface InsightBannerProps {
  type?: "info" | "success" | "warning" | "error";
  message: string;
  className?: string;
}

export function InsightBanner({
  type = "info",
  message,
  className,
}: InsightBannerProps) {
  const variants = {
    info: "bg-primary/10 border-primary/20 text-primary",
    success: "bg-bid/10 border-bid/20 text-bid-foreground",
    warning: "bg-yellow-500/10 border-yellow-500/20 text-yellow-600 dark:text-yellow-400",
    error: "bg-ask/10 border-ask/20 text-ask-foreground",
  };

  return (
    <div
      className={cn(
        "px-3 py-2 rounded-lg border text-xs",
        variants[type],
        className
      )}
    >
      {message}
    </div>
  );
}

/**
 * Chart explanation text for beneath visualizations
 */
interface ChartExplanationProps {
  children: ReactNode;
  className?: string;
}

export function ChartExplanation({ children, className }: ChartExplanationProps) {
  return (
    <p className={cn("text-[10px] text-muted-foreground mt-2 text-center", className)}>
      {children}
    </p>
  );
}

